import { forwardRef, useEffect, useMemo, useRef, useState } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import nProgress from "nprogress"
import toast from "react-hot-toast"
import { Transition } from "components"
import { lang } from "config"
import { useAuth } from "contexts/auth-context"
import { useResultSet } from "repositories/db-repository"
import useSupplierRepositories from "repositories/supplier"
import useBranchRepositories from "repositories/branch"
import SelectSearchable from "components/forms/select-searchable"
import ReturnProductPickerModal from "./product-picker-modal"
import Input from "components/forms/input"
import ErrorMessage from "components/forms/error-message"
import { getBranchList, getCurrentEntity } from "helpers/general-helper"
import { getUser } from "helpers/auth-helper"
import { defaultUomForEntity, loadMasterUomFactors, masterUomFactor, normalizeUomCode } from "helpers/uom-helper"

// 240726 (#7): satuan retur (PCS/LUSIN/PASANG/…) mengikuti master; faktor pcs
// dari helper. qty_selected DISIMPAN internal dalam PCS — selector hanya mengubah
// cara input/tampilan (qty ÷ faktor), payload qty_returned tetap PCS + tag uom.
// 270726 (permintaan user): opsi satuan disamakan dengan form PEMBELIAN — diambil
// dari MASTER satuan (GET /satuan lewat helpers/uom-helper), bukan 3 opsi hardcoded.
// Konstanta di bawah tinggal fallback saat master belum termuat / fetch gagal.
const RETUR_UOM_OPTIONS = ["PCS", "LUSIN", "PASANG"]
const qtyUomStr = (pcs, factor) => {
  const n = Math.round((num(pcs) / (factor || 1)) * 100) / 100
  return Number.isInteger(n) ? String(n) : n.toFixed(2)
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function todayISO() {
  const d = new Date()
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, "0")
  const dd = String(d.getDate()).padStart(2, "0")
  return `${yyyy}-${mm}-${dd}`
}

function num(v) {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

function isIntLike(v) {
  const n = num(v)
  return Math.abs(n - Math.round(n)) < 1e-9
}

async function apiFetch(path, { method = "GET", body, signal } = {}) {
  const base = (process.env.REACT_APP_INVENTORY_API_URL || "").replace(/\/$/, "")
  const url = `${base}${path}`
  // Sisipkan user_id dari sesi login UI (sumber data yang sama dengan useAuth())
  // ke body request tulis, supaya backend bisa mengisi created_by/updated_by.
  // Tanpa ini actor di backend jatuh ke "SYSTEM".
  const userId = getUser()?.id
  const payload = method !== "GET" && userId ? { user_id: userId, ...(body || {}) } : body
  const res = await fetch(url, {
    method,
    signal,
    headers: { "Content-Type": "application/json" },
    body: payload ? JSON.stringify(payload) : null,
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok) {
    const msg = json?.message || "Request gagal"
    const err = json?.error ? `: ${json.error}` : ""
    throw new Error(msg + err)
  }
  return json
}

/**
 * Normalize gr_items dari response API.
 *
 * Untuk edit route: backend sudah menyertakan qty_selected yang di-merge
 * dari return items — tinggal pakai langsung.
 *
 * Untuk create route (pickGR): qty_selected dari GR = 0 karena belum ada return.
 */
function normalizeGRItems(items = []) {
  return (Array.isArray(items) ? items : [])
    .map((x) => {
      const rawCost = x.unit_cost ?? x?.po_item?.cost_price ?? ""
      return {
        id: x.id || x.receipt_item_id,
        product_unit_id: x.product_unit_id,
        product_unit:
          x.product_unit?.name ||
          x.product_unit?.product_unit_name ||
          x.product_unit_name ||
          x.product_name ||
          "-",
        qty_received: num(x.qty_received),
        batch_no: x.batch_no || "",
        expired_date: x.expired_date ? String(x.expired_date).slice(0, 10) : "",
        qty_returnable: Math.max(0, num(x.qty_received) - num(x.qty_returned)),
        checked: num(x.qty_selected) > 0,
        qty_selected: num(x.qty_selected) || 0,
        unit_cost: rawCost === "" || rawCost == null ? "" : Math.trunc(num(rawCost)),
        notes: x.notes ?? "",
      }
    })
    .sort((a, b) => String(a.product_unit || "").localeCompare(String(b.product_unit || ""), undefined, { sensitivity: "base" }))
}

function normalizeManualReturnItems(items = []) {
  return (Array.isArray(items) ? items : [])
    .map((x) => {
      const productUnitId = x.product_unit_id ?? x.product_unit?.id ?? x.id
      const currentStock = num(
        x.qty_returnable ??
          x.current_stock ??
          x.available_stock ??
          x.stock ??
          x.qty_on_hand ??
          x.qty_received ??
          x.qty_returned
      )
      const qtySelected = num(x.qty_selected ?? x.qty_returned)
      const rawCost = x.unit_cost ?? x.latest_price ?? ""

      return {
        id: `manual:${productUnitId}`,
        receipt_item_id: null,
        product_unit_id: productUnitId,
        product_unit:
          x.product_unit?.name ||
          x.product_unit?.long_name ||
          x.product_unit_long_name ||
          x.product_unit_name ||
          x.product_name ||
          "-",
        qty_received: currentStock,
        batch_no: x.batch_no || "",
        expired_date: x.expired_date ? String(x.expired_date).slice(0, 10) : "",
        qty_returnable: currentStock,
        checked: qtySelected > 0,
        qty_selected: qtySelected,
        unit_cost: rawCost === "" || rawCost == null ? "" : Math.trunc(num(rawCost)),
        notes: x.notes ?? "",
        is_manual: true,
      }
    })
    .sort((a, b) => String(a.product_unit || "").localeCompare(String(b.product_unit || ""), undefined, { sensitivity: "base" }))
}

function fmtPrintDate(v) {
  if (!v) return "-"
  const d = new Date(v)
  if (Number.isNaN(d.getTime())) return String(v)
  return new Intl.DateTimeFormat("id-ID", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(d)
}

const PurchaseReturnPrintDocument = forwardRef(function PurchaseReturnPrintDocument(
  {
    returnNumber,
    status,
    sourceType,
    grNumber,
    poNumber,
    outletName,
    supplierName,
    returnDate,
    reason,
    items = [],
    totalQty = 0,
    totalAmount = 0,
    printedBy,
  },
  ref
) {
  const printedAt = new Intl.DateTimeFormat("id-ID", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date())

  const showUnitCost = sourceType !== "goods_receipt"
  // Saat showUnitCost: kolom = No | Produk | Qty | Unit Cost | Jumlah | Notes = 6.
  // Saat tidak: skip Unit Cost & Jumlah = 4.
  const colCount = showUnitCost ? 6 : 4

  const PAGE_SIZE = 20
  const chunks = []
  for (let i = 0; i < items.length; i += PAGE_SIZE) {
    chunks.push(items.slice(i, i + PAGE_SIZE))
  }
  if (chunks.length === 0) chunks.push([])

  const renderHeader = () => (
    <>
      <div className="flex items-start justify-between pb-4 border-b border-neutral-300">
        <div>
          <div className="text-xl font-bold tracking-wide">RETUR PEMBELIAN</div>
          <div className="text-[12px] text-neutral-600">Purchase Return</div>
        </div>
        <div className="text-right">
          <div className="text-[13px] font-semibold">{returnNumber || "-"}</div>
          <div className="text-[11px] text-neutral-600">
            Status: {String(status || "DRAFT").toUpperCase()}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 mt-4 gap-x-8 gap-y-1">
        <div className="flex">
          <div className="w-40 text-neutral-500">Tanggal Return</div>
          <div className="flex-1 font-medium">{fmtPrintDate(returnDate)}</div>
        </div>
        <div className="flex">
          <div className="w-40 text-neutral-500">Sumber</div>
          <div className="flex-1 font-medium">
            {sourceType === "goods_receipt" ? "Penerimaan Barang" : "Manual"}
          </div>
        </div>
        <div className="flex">
          <div className="w-40 text-neutral-500">Outlet / Gudang</div>
          <div className="flex-1 font-medium">{outletName || "-"}</div>
        </div>
        {sourceType === "goods_receipt" ? (
          <>
            <div className="flex">
              <div className="w-40 text-neutral-500">No. GR</div>
              <div className="flex-1 font-medium">{grNumber || "-"}</div>
            </div>
            <div className="flex">
              <div className="w-40 text-neutral-500">No. PO</div>
              <div className="flex-1 font-medium">{poNumber || "-"}</div>
            </div>
          </>
        ) : null}
        {supplierName ? (
          <div className="flex">
            <div className="w-40 text-neutral-500">Supplier</div>
            <div className="flex-1 font-medium">{supplierName}</div>
          </div>
        ) : null}
      </div>
    </>
  )

  return (
    <div ref={ref} className="hidden print:block">
      <style>{`
        @page { size: A4; margin: 16mm 14mm; }
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .pr-page { page-break-after: always; }
          .pr-page:last-child { page-break-after: auto; }
        }
      `}</style>

      {chunks.map((chunkItems, pageIdx) => {
        const isLastPage = pageIdx === chunks.length - 1
        const baseIndex = pageIdx * PAGE_SIZE

        return (
          <div key={pageIdx} className="pr-page text-[12px] text-neutral-900">
            {renderHeader()}

            <table className="w-full mt-5 border-collapse">
              <thead>
                <tr className="bg-neutral-100">
                  <th className="w-8 px-2 py-1 text-left border border-neutral-300">No</th>
                  <th className="px-2 py-1 text-left border border-neutral-300">Produk</th>
                  <th className="w-20 px-2 py-1 text-right border border-neutral-300">Qty</th>
                  {showUnitCost ? (
                    <>
                      <th className="w-24 px-2 py-1 text-right border border-neutral-300">Unit Cost</th>
                      <th className="w-28 px-2 py-1 text-right border border-neutral-300">Jumlah</th>
                    </>
                  ) : null}
                  <th className="px-2 py-1 text-left border border-neutral-300">Notes</th>
                </tr>
              </thead>
              <tbody>
                {chunkItems.length === 0 && (
                  <tr>
                    <td colSpan={colCount} className="px-2 py-3 text-center border border-neutral-300 text-neutral-500">
                      Tidak ada item
                    </td>
                  </tr>
                )}
                {chunkItems.map((it, i) => (
                  <tr key={it.id || it.product_unit_id || `${pageIdx}-${i}`}>
                    <td className="px-2 py-1 align-top border border-neutral-300">{baseIndex + i + 1}</td>
                    <td className="px-2 py-1 align-top border border-neutral-300">
                      {it.product_unit || it.product_unit_id}
                    </td>
                    <td className="px-2 py-1 text-right align-top border border-neutral-300">
                      {Number(it.qty_selected) || 0}
                    </td>
                    {showUnitCost ? (
                      <>
                        <td className="px-2 py-1 text-right align-top border border-neutral-300">
                          {it.unit_cost === "" || it.unit_cost == null ? "-" : Number(it.unit_cost).toLocaleString("id-ID")}
                        </td>
                        <td className="px-2 py-1 text-right align-top border border-neutral-300">
                          {it.unit_cost === "" || it.unit_cost == null
                            ? "-"
                            : (Number(it.qty_selected || 0) * Number(it.unit_cost || 0)).toLocaleString("id-ID")}
                        </td>
                      </>
                    ) : null}
                    <td className="px-2 py-1 align-top border border-neutral-300">{it.notes || "-"}</td>
                  </tr>
                ))}
              </tbody>
              {isLastPage ? (
                <tfoot>
                  <tr className="font-semibold bg-neutral-50">
                    <td colSpan={2} className="px-2 py-1 text-right border border-neutral-300">
                      Total Qty
                    </td>
                    <td className="px-2 py-1 text-right border border-neutral-300">{totalQty}</td>
                    {showUnitCost ? (
                      <>
                        {/* Unit Cost kosong, Jumlah = totalAmount */}
                        <td className="px-2 py-1 border border-neutral-300" />
                        <td className="px-2 py-1 text-right border border-neutral-300">
                          {Number(totalAmount || 0).toLocaleString("id-ID")}
                        </td>
                      </>
                    ) : null}
                    {/* Notes kosong */}
                    <td className="px-2 py-1 border border-neutral-300" />
                  </tr>
                </tfoot>
              ) : (
                <tfoot>
                  <tr>
                    <td colSpan={colCount} className="px-2 py-1 text-right border border-neutral-300 text-[11px] text-neutral-500 italic">
                      bersambung ke halaman berikutnya…
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>

            {isLastPage && reason ? (
              <div className="mt-4">
                <div className="text-neutral-500">Alasan Return</div>
                <div className="mt-1 whitespace-pre-wrap border border-neutral-200 rounded p-2 min-h-[48px]">
                  {reason}
                </div>
              </div>
            ) : null}

            {isLastPage ? (
              <div className="grid grid-cols-3 gap-6 mt-12 text-center">
                {["Disiapkan oleh", "Mengetahui", "Penerima"].map((role) => (
                  <div key={role}>
                    <div className="text-neutral-600">{role}</div>
                    <div className="h-16" />
                    <div className="pt-1">( _______________ )</div>
                  </div>
                ))}
              </div>
            ) : null}

            <div className="mt-8 pt-2 border-t border-neutral-200 flex items-center justify-between text-[10px] text-neutral-400">
              <span>
                {returnNumber ? `${returnNumber} • ` : ""}Dicetak{printedBy ? ` oleh ${printedBy}` : ""} pada {printedAt}
              </span>
              <span>Halaman {pageIdx + 1} dari {chunks.length}</span>
            </div>
          </div>
        )
      })}
    </div>
  )
})

// ─── Component ───────────────────────────────────────────────────────────────

export default function PurchaseReturnForm() {
  const { id } = useParams()
  const isEditRoute = Boolean(id)

  const { can, currentBranch, user } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()

  // 240726 (permintaan user #8): retur bisa memilih outlet SE-ENTITAS user
  // (bukan hanya outlet sendiri). Ambil daftar outlet lengkap lalu filter per
  // entity; backend guard men-scope hal yang sama.
  // 270726 (permintaan user): user VISION juga boleh memilih outlet HO entitas lain
  // (PT Atalla id 1, branches.is_ho=1) — barang Vision yang nyangkut di gudang HO
  // Atalla direturkan balik ke Vision. Aturan & daftar outlet SAMA dengan form
  // pembelian (pages/purchase/form.jsx: entity VISION → outlet ber-is_ho);
  // PurchaseReturnController::store memvalidasi hal yang sama.
  const { data: allOutletData } = useBranchRepositories({ paginate: false, unrestricted: true })
  const branchSelection = useMemo(() => {
    const rows = Array.isArray(allOutletData) ? allOutletData : allOutletData?.data ?? []
    const entity = getCurrentEntity()
    const filtered =
      entity === "VISION"
        ? rows.filter((b) => !!b?.is_ho || b?.entity === "VISION")
        : entity
        ? rows.filter((b) => !b?.entity || b.entity === entity)
        : rows
    return filtered.length ? filtered : (getBranchList() || [])
  }, [allOutletData])
  const branchIdList = useMemo(
    () => (branchSelection ? branchSelection.map((x) => x.id).filter(Boolean) : []),
    [branchSelection]
  )

  // ── Header state ──────────────────────────────────────────────────────────
  const [outletId, setOutletId] = useState(currentBranch?.id ?? null)
  // 240726 (#7): satuan GLOBAL retur (input qty dalam satuan ini; disimpan PCS).
  // 290726: default entitas diisi SINKRON saat mount (lihat ho-order/form).
  const [retUom, setRetUom] = useState(() => defaultUomForEntity(getCurrentEntity(), null))
  const retFactor = masterUomFactor(retUom)

  // 290726 (permintaan user): retur MANUAL mengikuti aturan yang sama dgn menu
  // pembelian — satuan ber-faktor 12 memakai dozen_store_price bila ada, selain itu
  // harga per-pcs. Nilai yang DISIMPAN tetap per PCS (tampilan dikali faktor).
  //
  // Sengaja TIDAK dipakai untuk retur dari GOODS RECEIPT: di sana harga mengikuti
  // harga beli asli (po_item.cost_price) dan input-nya memang dikunci.
  // Harga DASAR per PCS (store_price / latest_price) — acuan konversi antar satuan.
  const manualBasePcs = (row) => {
    const raw = row?.unit_cost ?? row?.latest_price ?? ""
    return raw === "" || raw == null ? "" : Math.trunc(num(raw))
  }

  // unit_cost yang DISIMPAN selalu per PCS. Untuk satuan ber-faktor 12 dgn harga
  // lusin tersedia, per-pcs = dozen / 12 supaya total retur = harga lusin x jml lusin.
  const costPerPcsFor = (uom, basePcs, dozen) => {
    const f = masterUomFactor(uom)
    if (f === 12 && num(dozen) > 0) return Math.round(num(dozen) / 12)
    return basePcs === "" || basePcs == null ? "" : Math.trunc(num(basePcs))
  }

  const manualCostPerPcs = (row) =>
    costPerPcsFor(retUom, manualBasePcs(row), row?.dozen_store_price)
  // 270726: opsi satuan dari MASTER satuan (GET /satuan) — sama seperti form
  // pembelian. null = belum termuat → pakai fallback RETUR_UOM_OPTIONS. qty di state
  // selalu PCS, jadi faktor yang datang belakangan cukup mengubah tampilan.
  const [satuanOptions, setSatuanOptions] = useState(null)
  useEffect(() => {
    let cancelled = false
    loadMasterUomFactors().then((names) => {
      if (cancelled) return
      const opts = names.length ? names : [...RETUR_UOM_OPTIONS]
      setSatuanOptions(opts)
      // 290726: satuan default ikut ENTITAS user (VISION→PASANG, ATALLA→LUSIN).
      setRetUom((prev) => normalizeUomCode(prev) || prev)
    })
    return () => { cancelled = true }
  }, [])
  const [returnDate, setReturnDate] = useState(todayISO())
  const [reason, setReason] = useState("")
  const [status, setStatus] = useState("DRAFT")
  const [returnId, setReturnId] = useState(id || null)
  const [returnNumber, setReturnNumber] = useState("")
  const [sourceType, setSourceType] = useState("manual")

  // ── Selected GR ───────────────────────────────────────────────────────────
  const [selectedGR, setSelectedGR] = useState(null)
  const receiptId = selectedGR?.id ?? null

  // ── GR items ──────────────────────────────────────────────────────────────
  const [grItems, setGrItems] = useState([])

  // ── UI ────────────────────────────────────────────────────────────────────
  const [errors, setErrors] = useState({})
  const [acting, setActing] = useState(false)

  // ── GR picker modal ───────────────────────────────────────────────────────
  const [grModalOpen, setGrModalOpen] = useState(false)
  const [page, setPage] = useState(1)
  const [searchParam, setSearchParam] = useState("")
  const pickReqRef = useRef(0)
  const [initialLoading, setInitialLoading] = useState(isEditRoute)
  const [productSearch, setProductSearch] = useState("")
  const [productSearchInput, setProductSearchInput] = useState("")
  const [selectedProductUnitId, setSelectedProductUnitId] = useState("")
  const [selectedProductRow, setSelectedProductRow] = useState(null)
  const [manualQty, setManualQty] = useState("")
  // 270726: modal picker multi-select (pola "Pilih Banyak" form pembelian).
  const [showBulkPicker, setShowBulkPicker] = useState(false)
  // ATALLA-258: retur MANUAL harus bisa memilih supplier tujuan — tanpa ini backend
  // default ke supplier 1 (Konsinyasi Atalla, eksternal) sehingga retur ke Vision
  // tidak pernah menambah stok Vision (mirror HO_PR_IN butuh supplier internal).
  const [supplierId, setSupplierId] = useState("")
  const { data: supplierSelection } = useSupplierRepositories({ paginate: false })
  const supplierOptions = useMemo(
    () => (Array.isArray(supplierSelection) ? supplierSelection : supplierSelection?.data ?? []),
    [supplierSelection]
  )
  const [manualProductRows, setManualProductRows] = useState([])
  const [isLoadingManualProducts, setIsLoadingManualProducts] = useState(false)
  const [manualProductPage, setManualProductPage] = useState(1)
  const [manualProductMeta, setManualProductMeta] = useState({})
  const manualProductSearchDebounceRef = useRef(null)

  const isDraft = String(status || "").toUpperCase() === "DRAFT"
  const isLocked = !isDraft

  // 270726 (permintaan user): outlet terpilih milik ENTITAS LAIN (mis. user Vision
  // memilih outlet HO PT Atalla id 1). Retur seperti ini wajib ditujukan ke supplier
  // internal entitas user (Vision One) supaya stok balik ke entitasnya saat posting —
  // backend menolak supplier di luar entitas untuk kasus ini.
  const isCrossEntityOutlet = useMemo(() => {
    const oid = outletId?.id ?? outletId
    const entity = getCurrentEntity()
    if (!oid || !entity) return false
    const found = (branchSelection || []).find((b) => String(b?.id) === String(oid))
    return Boolean(found?.entity) && found.entity !== entity
  }, [outletId, branchSelection])

  // Preselect supplier internal entitas user supaya user tidak perlu menebak.
  useEffect(() => {
    if (!isCrossEntityOutlet || supplierId || isLocked) return

    const ownBranchId = currentBranch?.id
    const ownBranchName = String(currentBranch?.name || "").toLowerCase()
    const match =
      supplierOptions.find(
        (s) => ownBranchId != null && String(s?.linked_branch_id ?? "") === String(ownBranchId)
      ) ||
      supplierOptions.find((s) => String(s?.name || "").toLowerCase() === ownBranchName)

    if (match?.id != null) setSupplierId(String(match.id))
  }, [isCrossEntityOutlet, supplierOptions, supplierId, isLocked, currentBranch])

  // ── Print PDF ─────────────────────────────────────────────────────────────
  const outletName = useMemo(() => {
    const oid = outletId?.id ?? outletId
    if (oid == null) return ""
    const found = (branchSelection || []).find((b) => String(b?.id) === String(oid))
    return found?.name || ""
  }, [outletId, branchSelection])

  const supplierName = useMemo(() => {
    return (
      selectedGR?.supplier_name ||
      selectedGR?.supplier_name_snapshot ||
      selectedGR?.purchase_order?.supplier?.name ||
      ""
    )
  }, [selectedGR])

  const grNumber = selectedGR?.gr_number || selectedGR?.id || ""
  const poNumber = selectedGR?.purchase_order?.po_number || selectedGR?.po_number || selectedGR?.po_id || ""

  const printItems = useMemo(
    () => grItems.filter((x) => x.checked && num(x.qty_selected) > 0),
    [grItems]
  )

  const printTotalQty = useMemo(
    () => printItems.reduce((sum, x) => sum + num(x.qty_selected), 0),
    [printItems]
  )

  const printTotalAmount = useMemo(
    () => printItems.reduce((sum, x) => sum + num(x.qty_selected) * num(x.unit_cost), 0),
    [printItems]
  )

  const printRef = useRef()
  // 250726 (permintaan user): cetak retur pembelian pakai FORMAT yang SAMA dgn retur
  // toko offline → buka PDF backend (blade pdf.offline_return_faktur, header data-driven)
  // di tab baru, bukan react-to-print client-side lagi.
  const onPrint = () => {
    if (!returnId) return toast.error("Simpan retur dulu sebelum cetak")
    const base = (process.env.REACT_APP_INVENTORY_API_URL || "").replace(/\/$/, "")
    window.open(`${base}/purchase-returns/${returnId}/print`, "_blank")
  }

  // ── Load existing return (edit route) ─────────────────────────────────────
  //
  // Response yang dikonfirmasi dari API:
  // {
  //   data: {
  //     return: { id, receipt_id, outlet_id, status, return_date, reason, items: [...] },
  //     gr:     { id, gr_number, po_id, receipt_date, ... },
  //     items:  [...],   ← return items (sama dengan return.items)
  //     gr_items: [      ← GR items + qty_selected sudah di-merge backend ✅
  //       { id, product_unit_id, product_unit, qty_received, qty_selected, unit_cost, ... }
  //     ]
  //   }
  // }
  //
  // Fix utama: HANYA pakai payload.gr_items, TIDAK fetch /goods-receipts/:id lagi.
  // Fetch ulang GR items itulah yang menyebabkan grItems di-overwrite checked:false
  // di production karena async race condition.
  // ─────────────────────────────────────────────────────────────────────────
    useEffect(() => {
      if (!isEditRoute) {
        setInitialLoading(false)
        return
      }

      const run = async () => {
        try {
          setInitialLoading(true)
          nProgress.start()

          const res = await apiFetch(`/purchase-returns/${id}`)
          const payload = res?.data || res
          const ret = payload?.return || payload

          setReturnId(ret?.id ?? id)
          setReturnNumber(ret?.return_number || "")
          setStatus(ret?.status ?? "DRAFT")
          setSourceType(
            ret?.source_type ||
              payload?.source_type ||
              (ret?.receipt_id || payload?.gr ? "goods_receipt" : "manual")
          )
          if (ret?.outlet_id != null) setOutletId(ret.outlet_id)
          // 240726 (#7): pulihkan satuan global dari item pertama (fallback PCS).
          // 270726: terima satuan APA PUN (master/legacy) seperti form pembelian —
          // dulu dibatasi 3 opsi hardcoded sehingga retur bersatuan master (mis. GRS)
          // balik tampil sebagai PCS saat dibuka lagi.
          {
            // 290726: label lama dipetakan ke kode master (LUSIN→LSN, PASANG→PSG).
            const firstUom = normalizeUomCode(ret?.items?.[0]?.uom || "")
            if (firstUom) setRetUom(firstUom)
          }
          if (ret?.return_date) setReturnDate(String(ret.return_date).slice(0, 10))
          if (ret?.reason != null) setReason(ret.reason || "")
          if (ret?.supplier_id != null) setSupplierId(String(ret.supplier_id))

          const gr = payload?.gr || ret?.goods_receipt || null
          if (gr) {
            setSelectedGR(gr)
          } else if (ret?.receipt_id) {
            setSelectedGR({ id: ret.receipt_id })
          }

          const returnItems = Array.isArray(payload?.items)
            ? payload.items
            : Array.isArray(ret?.items)
            ? ret.items
            : []

          // 100726 (ATALLA-312): dokumen terkunci (bukan DRAFT) merender baris retur
          // MILIKNYA sendiri, bukan merge goods_receipt_items — GR yang di-sync ulang
          // mengganti id item sehingga merge kosong dan produk "hilang" dari layar
          // padahal purchase_return_items masih utuh.
          const lockedDoc = (ret?.status ?? "DRAFT") !== "DRAFT"
          if (lockedDoc && returnItems.length > 0) {
            setGrItems(normalizeManualReturnItems(returnItems))
            return
          }

          const grItemsRaw = payload?.gr_items
          if (Array.isArray(grItemsRaw) && grItemsRaw.length > 0) {
            setGrItems(normalizeGRItems(grItemsRaw))
            return
          }

          if ((!gr || !ret?.receipt_id) && returnItems.length > 0) {
            setGrItems(normalizeManualReturnItems(returnItems))
            return
          }

          const receiptIdFallback = ret?.receipt_id ?? gr?.id ?? null

          if (receiptIdFallback) {
            try {
              const resGR = await apiFetch(`/goods-receipts/${receiptIdFallback}`)
              const payloadGR = resGR?.data || resGR
              const itemsGR = payloadGR?.items || payloadGR?.receipt_items || []

              if (gr == null) {
                setSelectedGR((prev) => ({ ...payloadGR, ...(prev || {}) }))
              }

              const draftMap = new Map()
              for (const it of returnItems) {
                const rid = String(it.receipt_item_id || "")
                if (!rid) continue

                draftMap.set(rid, {
                  qty_selected: num(it.qty_returned),
                  unit_cost: it.unit_cost ?? "",
                  notes: it.notes ?? "",
                })
              }

              setGrItems(
                (Array.isArray(itemsGR) ? itemsGR : [])
                  .map((x) => {
                    const rid = String(x.id || x.receipt_item_id || "")
                    const draft = draftMap.get(rid)

                    return {
                      id: x.id || x.receipt_item_id,
                      product_unit_id: x.product_unit_id,
                      product_unit:
                        x.product_unit?.name || x.product_unit_name || x.product_name || "-",
                      qty_received: num(x.qty_received),
                      batch_no: x.batch_no || "",
                      expired_date: x.expired_date ? String(x.expired_date).slice(0, 10) : "",
                      qty_returnable: x.qty_received - (x?.qty_returned ?? 0) ?? 0,
                      checked: draft ? num(draft.qty_selected) > 0 : false,
                      qty_selected: draft ? num(draft.qty_selected) : 0,
                      unit_cost: draft?.unit_cost ?? x.unit_cost ?? x?.po_item?.cost_price ?? "",
                      notes: draft?.notes ?? x.notes ?? "",
                    }
                  })
                  .sort((a, b) => String(a.product_unit || "").localeCompare(String(b.product_unit || ""), undefined, { sensitivity: "base" }))
              )
            } catch (e) {
              toast.error(e?.message || "Gagal memuat item GR")
            }
          }
        } catch (e) {
          toast.error(e?.message || "Gagal memuat purchase return")
        } finally {
          setInitialLoading(false)
          nProgress.done()
        }
      }

      run()
    }, [isEditRoute, id])

  // ── GR List (modal) ───────────────────────────────────────────────────────
  const grQuery = useMemo(() => {
    if (!outletId || sourceType !== "goods_receipt") return null
    const oid = outletId?.id ?? outletId
    return {
      outlet_id: oid,
      branch_list: JSON.stringify(branchIdList),
      page,
      per_page: 5,
      search: searchParam || undefined,
      status: "POSTED",
      po_type: "REGULAR",
    }
  }, [outletId, branchIdList, page, searchParam, sourceType])

  const { data: gr_result_set, isLoading: isLoadingGR } = useResultSet(
    `${process.env.REACT_APP_INVENTORY_API_URL}`,
    `goods-receipts`,
    grQuery
  )

  useEffect(() => {
    if (isLoadingGR) nProgress.start()
    else nProgress.done()
  }, [isLoadingGR])

  const grList = useMemo(() => {
    const arr = gr_result_set?.data?.data
    return Array.isArray(arr) ? arr : []
  }, [gr_result_set])

  const grMeta = gr_result_set?.data || {}
  const canPrev = Boolean(grMeta?.prev_page_url)
  const canNext = Boolean(grMeta?.next_page_url)

  const [searchInputValue, setSearchInputValue] = useState("")

  const onSearchChange = (val) => setSearchInputValue(String(val || ""))

  const onSearchSubmit = () => {
    setSearchParam(String(searchInputValue || ""))
    setPage(1)
  }

  const onManualProductSearchChange = (val) => {
    setProductSearchInput(String(val || ""))

    if (manualProductSearchDebounceRef.current) {
      clearTimeout(manualProductSearchDebounceRef.current)
    }

    manualProductSearchDebounceRef.current = setTimeout(() => {
      setProductSearch(String(val || "").trim())
      setManualProductPage(1)
    }, 400)
  }

  useEffect(() => {
    if (!outletId || sourceType !== "manual") {
      setManualProductRows([])
      setIsLoadingManualProducts(false)
      setManualProductMeta({})
      return
    }

    const ctrl = new AbortController()
    const run = async () => {
      try {
        setIsLoadingManualProducts(true)

        const params = new URLSearchParams()
        params.set("outlet_id", String(outletId?.id ?? outletId))
        params.set("only_in_stock", "1")
        params.set("aggregate", "1")
        params.set("paginate", "true")
        params.set("page", String(manualProductPage))
        params.set("limit", "15")
        if (productSearch) params.set("search", productSearch)

        const res = await apiFetch(`/product-unit-stocks?${params.toString()}`, {
          method: "GET",
          signal: ctrl.signal,
        })

        const payload = res?.data || res
        if (Array.isArray(payload?.data)) {
          setManualProductRows(payload.data)
          setManualProductMeta(payload)
        } else if (Array.isArray(payload)) {
          setManualProductRows(payload)
          setManualProductMeta({})
        } else {
          setManualProductRows([])
          setManualProductMeta({})
        }
      } catch (e) {
        if (e?.name !== "AbortError") {
          toast.error(e?.message || "Gagal memuat stok produk")
        }
      } finally {
        setIsLoadingManualProducts(false)
      }
    }

    run()
    return () => ctrl.abort()
  }, [outletId, productSearch, sourceType, manualProductPage])

  useEffect(() => {
    if (!selectedProductUnitId) {
      setSelectedProductRow(null)
      return
    }

    const found = manualProductRows.find(
      (row) =>
        String(row?.product_unit_id ?? row?.id ?? "") === String(selectedProductUnitId)
    )

    if (found) setSelectedProductRow(found)
  }, [manualProductRows, selectedProductUnitId])

  // ── Pick GR from modal (create route) ─────────────────────────────────────
  const pickGR = async (gr) => {
    const reqId = ++pickReqRef.current

    setSelectedGR(gr)
    setGrModalOpen(false)
    setGrItems([])

    try {
      nProgress.start()
      const res = await apiFetch(`/goods-receipts/${gr.id}`)
      if (reqId !== pickReqRef.current) return // abaikan response lama

      const payload = res?.data || res
      const items = payload?.items || payload?.receipt_items || []

      if (!Array.isArray(items) || items.length === 0) {
        toast.error(`GR ${gr.gr_number || gr.id} tidak memiliki item.`)
        return
      }

      // GR baru dipilih untuk create: qty_selected = 0, checked = false
      setGrItems(normalizeGRItems(items))
    } catch (e) {
      toast.error(e?.message || "Gagal memuat item GR")
    } finally {
      nProgress.done()
    }
  }

  const addManualItem = () => {
    if (!outletId) {
      toast.error("Pilih outlet terlebih dahulu")
      return
    }

    if (!selectedProductUnitId) {
      toast.error("Pilih produk terlebih dahulu")
      return
    }

    const row =
      selectedProductRow ||
      manualProductRows.find(
        (item) =>
          String(item?.product_unit_id ?? item?.id ?? "") === String(selectedProductUnitId)
      )

    if (!row) {
      toast.error("Produk tidak ditemukan")
      return
    }

    const productUnitId = row?.product_unit_id ?? row?.id
    const currentStock = num(
      row?.current_stock ?? row?.available_stock ?? row?.stock ?? row?.qty_on_hand
    )

    if (!productUnitId) {
      toast.error("Produk tidak valid")
      return
    }

    if (currentStock <= 0) {
      toast.error("Stok produk kosong")
      return
    }

    const nextQty = Math.max(1, Math.min(num(manualQty) || 1, currentStock))

    setGrItems((prev) => {
      const existingIndex = prev.findIndex(
        (item) => String(item.product_unit_id) === String(productUnitId)
      )

      if (existingIndex >= 0) {
        return prev.map((item, index) =>
          index !== existingIndex
            ? item
            : {
                ...item,
                checked: true,
                qty_received: currentStock,
                qty_returnable: currentStock,
                qty_selected: Math.min(
                  currentStock,
                  Math.max(1, num(item.qty_selected) + nextQty)
                ),
              }
        )
      }

      return [
        {
          id: `manual:${productUnitId}`,
          receipt_item_id: null,
          product_unit_id: productUnitId,
          product_unit:
            row?.product_unit?.name ||
            row?.product_unit?.long_name ||
            row?.product_unit_long_name ||
            row?.product_unit_name ||
            row?.name ||
            "-",
          qty_received: currentStock,
          batch_no: row?.batch_no || "",
          expired_date: row?.expired_date ? String(row.expired_date).slice(0, 10) : "",
          qty_returnable: currentStock,
          checked: true,
          qty_selected: nextQty,
          unit_cost: manualCostPerPcs(row),
          // 290726: acuan konversi antar satuan — tanpa ini, LUSIN -> PCS memakai
          // dozen/12 dan bukan harga pcs aslinya.
          base_pcs_price: manualBasePcs(row),
          dozen_store_price: num(row?.dozen_store_price),
          notes: "",
          is_manual: true,
        },
        ...prev,
      ]
    })

    setSelectedProductUnitId("")
    setSelectedProductRow(null)
    setManualQty("")
    setProductSearch("")
    setProductSearchInput("")
  }

  /**
   * 270726 (permintaan user): tambah BANYAK produk sekaligus dari picker multi-select
   * (pola "Pilih Banyak" di form pembelian). `entries` = [{ product, qty_pcs }] — qty
   * sudah dikonversi ke PCS oleh modal. Merge per product unit mengikuti perilaku
   * addManualItem: unit yang sudah ada qty-nya dijumlahkan, bukan baris dobel.
   */
  const addManualItemsBulk = (entries) => {
    if (!entries?.length) return

    setGrItems((prev) => {
      let next = [...prev]

      for (const { product: row, qty_pcs: qtyPcs } of entries) {
        const productUnitId = row?.product_unit_id ?? row?.id
        if (!productUnitId) continue

        const currentStock = num(
          row?.current_stock ?? row?.available_stock ?? row?.stock ?? row?.qty_on_hand
        )
        if (currentStock <= 0) continue

        const addQty = Math.max(1, Math.min(num(qtyPcs) || 1, currentStock))
        const existingIndex = next.findIndex(
          (item) => String(item.product_unit_id) === String(productUnitId)
        )

        if (existingIndex >= 0) {
          next[existingIndex] = {
            ...next[existingIndex],
            checked: true,
            qty_received: currentStock,
            qty_returnable: currentStock,
            qty_selected: Math.min(
              currentStock,
              Math.max(1, num(next[existingIndex].qty_selected) + addQty)
            ),
          }
          continue
        }

        next = [
          {
            id: `manual:${productUnitId}`,
            receipt_item_id: null,
            product_unit_id: productUnitId,
            product_unit:
              row?.product_unit?.name ||
              row?.product_unit?.long_name ||
              row?.product_unit_long_name ||
              row?.product_unit_name ||
              row?.long_name ||
              row?.name ||
              "-",
            qty_received: currentStock,
            batch_no: row?.batch_no || "",
            expired_date: row?.expired_date ? String(row.expired_date).slice(0, 10) : "",
            qty_returnable: currentStock,
            checked: true,
            qty_selected: addQty,
            unit_cost: manualCostPerPcs(row),
            base_pcs_price: manualBasePcs(row),
            dozen_store_price: num(row?.dozen_store_price),
            notes: "",
            is_manual: true,
          },
          ...next,
        ]
      }

      return next
    })

    toast.success(`${entries.length} produk ditambahkan`)
    setShowBulkPicker(false)
  }

  // ── UI helpers ────────────────────────────────────────────────────────────
  const toggleItem = (receipt_item_id, checked) => {
    setGrItems((prev) =>
      prev.map((r) => {
        if (String(r.id) !== String(receipt_item_id)) return r

        const maxQty = num(r.qty_returnable ?? r.qty_received)
        const current = num(r.qty_selected)
        const safeQty = Math.max(0, Math.min(current || 1, maxQty))

        return {
          ...r,
          checked,
          qty_selected: checked ? safeQty : 0,
        }
      })
    )
  }

  const updateItem = (receipt_item_id, patch) => {
    setGrItems((prev) =>
      prev.map((r) => (String(r.id) === String(receipt_item_id) ? { ...r, ...patch } : r))
    )
  }

  const selectedCount = useMemo(
    () => grItems.filter((x) => x.checked && num(x.qty_selected) > 0).length,
    [grItems]
  )
  const selectedQtyTotal = useMemo(
    () => grItems.reduce((acc, x) => acc + (x.checked ? num(x.qty_selected) : 0), 0),
    [grItems]
  )

  // ── Validation ────────────────────────────────────────────────────────────
  const validateBeforeCreate = () => {
    const e = {}
    if (!outletId) e.outlet_id = "Outlet " + lang.field_required
    if (sourceType === "goods_receipt" && !receiptId) {
      e.receipt_id = "Goods Receipt " + lang.field_required
    }
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const buildItemsPayload = () => {
    const picked = grItems
      .filter((x) => x.checked && num(x.qty_selected) > 0)
      .map((x) => {
        const base = {
          // qty_selected disimpan PCS → kirim apa adanya; uom hanya penanda dokumen.
          qty_returned: num(x.qty_selected),
          uom: retUom,
          unit_cost: x.unit_cost === "" || x.unit_cost == null ? null : num(x.unit_cost),
          notes: x.notes || null,
        }

        if (sourceType === "manual" || x.is_manual) {
          return {
            ...base,
            product_unit_id: x.product_unit_id,
          }
        }

        return {
          ...base,
          receipt_item_id: x.id,
        }
      })
    return { items: picked }
  }

  const validateBeforeSaveDraft = (payload) => {
    const e = {}
    if (sourceType === "goods_receipt" && !receiptId) {
      e.receipt_id = "Goods Receipt " + lang.field_required
    }

    const items = Array.isArray(payload?.items) ? payload.items : []
    if (!items.length) e.items = "Pilih minimal 1 produk untuk direturn"

    for (let i = 0; i < items.length; i++) {
      const it = items[i]
      const q = num(it.qty_returned)

      if (sourceType === "goods_receipt" && !it.receipt_item_id) {
        e[`items.${i}.receipt_item_id`] = "receipt_item_id wajib"
        continue
      }

      if (sourceType === "manual" && !it.product_unit_id) {
        e[`items.${i}.product_unit_id`] = "product_unit_id wajib"
        continue
      }

      const source = sourceType === "manual"
        ? grItems.find((x) => String(x.product_unit_id) === String(it.product_unit_id))
        : grItems.find((x) => String(x.id) === String(it.receipt_item_id))
      const maxQty = num(source?.qty_returnable ?? source?.qty_received)

      if (q <= 0) e[`items.${i}.qty_returned`] = "Qty harus > 0"
      else if (!isIntLike(q)) e[`items.${i}.qty_returned`] = "Qty harus bilangan bulat"
      else if (q > maxQty) {
        e[`items.${i}.qty_returned`] = `Qty return tidak boleh melebihi ${maxQty}`
      }
    }

    setErrors(e)
    return Object.keys(e).length === 0
  }

  // ── Actions ───────────────────────────────────────────────────────────────
  const createReturnFull = async () => {
    if (!validateBeforeCreate()) return
    const payloadItems = buildItemsPayload()
    if (!validateBeforeSaveDraft(payloadItems)) return

    try {
      setActing(true)
      nProgress.start()
      toast.loading("Membuat return...", { id: "pr-create" })

      const body = {
        source_type: sourceType,
        outlet_id: outletId?.id ?? outletId,
        return_date: returnDate,
        reason: reason || null,
        // Mode manual: kirim supplier tujuan (ATALLA-258). Kosong = default backend
        // (Konsinyasi Atalla). Mode GR: supplier ikut PO-nya, tidak dikirim.
        supplier_id: sourceType === "manual" && supplierId ? supplierId : undefined,
        items: payloadItems.items,
      }

      const res =
        sourceType === "goods_receipt"
          ? await apiFetch(`/goods-receipts/${receiptId}/purchase-returns/create-full`, {
              method: "POST",
              body,
            })
          : await apiFetch(`/purchase-returns`, {
              method: "POST",
              body,
            })

      const data = res?.data || res
      const newId = data?.return?.id || data?.id

      toast.success(res?.message || "Return berhasil dibuat", { id: "pr-create" })

      if (newId) {
        setReturnId(newId)
        setReturnNumber(data?.return?.return_number || "")
        setStatus(data?.return?.status || "DRAFT")

        if (Array.isArray(data?.gr_items) && data.gr_items.length > 0) {
          setGrItems(normalizeGRItems(data.gr_items))
        }

        if (!isEditRoute) {
          navigate(`/return`, {
            replace: true,
            state: { back: "Daftar Retur", from: location.pathname, transition: "slide" },
          })
        }
      }
    } catch (e) {
      toast.error(e?.message || "Gagal membuat return", { id: "pr-create" })
    } finally {
      nProgress.done()
      setActing(false)
    }
  }

  const saveDraft = async () => {
    const payload = buildItemsPayload()
    if (!validateBeforeSaveDraft(payload)) return
    if (isLocked) return

    try {
      setActing(true)
      nProgress.start()
      toast.loading("Menyimpan draft...", { id: "pr-save" })

      const res = await apiFetch(`/purchase-returns/${returnId}/items`, {
        method: "POST",
        // 280726 (laporan user): supplier tujuan retur TIDAK ikut tersimpan saat
        // Simpan Draft — endpoint items dulu hanya meng-update item. Kirim supplier
        // terpilih (mode manual) supaya header ikut diperbarui backend.
        body: sourceType === "manual"
          ? { ...payload, supplier_id: supplierId || null }
          : payload,
      })

      toast.success(res?.message || "Draft tersimpan", { id: "pr-save" })

      // ATALLA-291: setelah simpan draft, keluar dari halaman edit kembali ke list
      // (dulu tetap di halaman edit sehingga user mengira belum tersimpan).
      navigate("/return", { state: location.state, replace: true })
    } catch (e) {
      toast.error(e?.message || "Gagal menyimpan draft", { id: "pr-save" })
    } finally {
      nProgress.done()
      setActing(false)
    }
  }

  const postReturn = async () => {
    if (!returnId) return
    if (!can?.("return-order.post")) return
    if (isLocked) return
    if (!window.confirm(`Post Return ${returnId}?`)) return

    try {
      setActing(true)
      nProgress.start()
      toast.loading("Memposting...", { id: "pr-post" })

      const res = await apiFetch(`/purchase-returns/${returnId}/post`, { method: "POST" })
      const data = res?.data || res

      toast.success(res?.message || "Return berhasil di-post", { id: "pr-post" })
      setStatus(data?.status || "POSTED")
    } catch (e) {
      toast.error(e?.message || "Gagal memposting return", { id: "pr-post" })
    } finally {
      nProgress.done()
      setActing(false)
    }
  }

  const cancelReturn = async () => {
    if (!returnId) return
    if (!can?.("purchase-return.cancel")) return
    if (String(status).toUpperCase() === "CANCELLED") return

    const r = window.prompt("Alasan pembatalan (opsional):", "")
    if (r === null) return

    try {
      setActing(true)
      nProgress.start()
      toast.loading("Membatalkan...", { id: "pr-cancel" })

      const res = await apiFetch(`/purchase-returns/${returnId}/cancel`, {
        method: "POST",
        body: r ? { reason: r } : null,
      })
      const data = res?.data || res

      toast.success(res?.message || "Return berhasil dibatalkan", { id: "pr-cancel" })
      setStatus(data?.status || "CANCELLED")
    } catch (e) {
      toast.error(e?.message || "Gagal membatalkan return", { id: "pr-cancel" })
    } finally {
      nProgress.done()
      setActing(false)
    }
  }

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-sm text-neutral-500">Memuat purchase return...</div>
      </div>
    )
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Transition type="fade">
      <div className="pb-8 space-y-6">

        {/* ── Page header ─────────────────────────────────────────────────── */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-lg font-semibold text-neutral-900">
              {returnId ? "Edit Retur" : "Buat Retur"}
              <span className="ml-2 text-xs font-medium text-neutral-500">
                {sourceType === "goods_receipt"
                  ? "(Outlet -> GR POSTED -> pilih produk)"
                  : "(Outlet -> pilih produk langsung)"}
              </span>
            </h1>
            <p className="text-xs text-neutral-500">
              Qty return harus bilangan bulat. Posting akan mengurangi stok dan menulis stock_transaction.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate("/return", { state: location.state })}
              className="px-4 py-3 text-xs font-semibold text-neutral-700 border border-neutral-200 rounded-xl hover:bg-neutral-50"
            >
              Back
            </button>

            {/* ATALLA-292: Print PDF hanya untuk retur yang sudah POSTED (draft belum final). */}
            {returnId && String(status).toUpperCase() === "POSTED" && (
              <button
                type="button"
                onClick={onPrint}
                className="inline-flex items-center gap-2 px-4 py-3 text-xs font-semibold border text-neutral-700 border-neutral-200 rounded-xl hover:bg-neutral-50"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-4 h-4 text-neutral-500"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M6 9V2h12v7" />
                  <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                  <path d="M6 14h12v8H6z" />
                </svg>
                Print PDF
              </button>
            )}

            {can?.("return-order.cancel") && returnId && String(status).toUpperCase() === "DRAFT" && (
              <button
                type="button"
                disabled={acting}
                onClick={cancelReturn}
                className="px-4 py-3 text-xs font-semibold text-white bg-red-600 rounded-xl hover:bg-red-500 disabled:opacity-60"
              >
                {acting ? "..." : "Batal"}
              </button>
            )}

            {can?.("return-order.post") && returnId && String(status).toUpperCase() === "DRAFT" && (
              <button
                type="button"
                disabled={acting}
                onClick={postReturn}
                className="px-4 py-3 text-xs font-semibold text-white bg-green-700 rounded-xl hover:bg-green-600 disabled:opacity-60"
              >
                {acting ? "..." : "Posting"}
              </button>
            )}

            {(can?.("return-order.create") || can?.("return-order.update")) && returnId && (
              <button
                type="button"
                disabled={acting || isLocked}
                onClick={saveDraft}
                className={`px-4 py-3 text-xs font-semibold text-white rounded-xl ${
                  isLocked
                    ? "bg-neutral-400 cursor-not-allowed"
                    : "bg-brand-900 hover:bg-brand-800"
                } disabled:opacity-60`}
              >
                {acting ? "..." : "Simpan Draft"}
              </button>
            )}

            {(can?.("return-order.create") || can?.("return-order.update")) && !returnId && (
              <button
                type="button"
                disabled={
                  acting ||
                  !outletId ||
                  (sourceType === "goods_receipt" && !receiptId) ||
                  selectedCount === 0
                }
                onClick={createReturnFull}
                className={`px-4 py-3 text-xs font-semibold text-white rounded-xl ${
                  outletId &&
                  (sourceType !== "goods_receipt" || receiptId) &&
                  selectedCount > 0
                    ? "bg-brand-900 hover:bg-brand-800"
                    : "bg-neutral-400 cursor-not-allowed"
                } disabled:opacity-60`}
              >
                {acting ? "..." : "Buat Return"}
              </button>
            )}
          </div>
        </div>

        {/* ── Header form ─────────────────────────────────────────────────── */}
        <div className="border border-neutral-200 rounded-xl">
          <div className="px-4 py-5 sm:p-6 grid grid-cols-2 gap-4">

            <div>
              <label className="block text-xs text-neutral-700">
                {lang.outlet_name}<sup className="text-xs text-red-700"> *</sup>
              </label>
              <SelectSearchable
                disabled={Boolean(returnId)}
                filter={(value) => value.name}
                onChange={(value) => {
                  const nextOutletId = value?.id ?? value ?? null

                  setOutletId(nextOutletId)

                  // edit route: abaikan reset apa pun
                  if (isEditRoute) return

                  setSelectedGR(null)
                  setGrItems([])
                  setErrors((prev) => ({ ...prev, receipt_id: undefined, items: undefined }))
                  setPage(1)
                  setSearchParam("")
                }}
                selection={branchSelection}
                isLoading={false}
                value={outletId}
                keyValue={(value) => value.id}
                title={(value) => value.name}
                placeholder="-- Pilih Outlet --"
              />
              <ErrorMessage error={errors?.outlet_id} />
            </div>

            <div>
              <label className="block text-xs text-neutral-700">Tanggal Return</label>
              <input
                type="date"
                disabled={Boolean(returnId)}
                value={returnDate}
                onChange={(e) => setReturnDate(e.target.value)}
                className="block w-full px-4 py-2 mt-1 text-sm transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200 disabled:bg-neutral-200"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-xs text-neutral-700">
                Sumber Return<sup className="text-xs text-red-700"> *</sup>
              </label>
              <div className="mt-1 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  disabled={Boolean(returnId)}
                  onClick={() => {
                    setSourceType("manual")
                    setSelectedGR(null)
                    setGrItems([])
                    setErrors((prev) => ({ ...prev, receipt_id: undefined, items: undefined }))
                  }}
                  className={`px-4 py-3 text-xs font-semibold border rounded-xl ${
                    sourceType === "manual"
                      ? "border-brand-900 bg-brand-900 text-white"
                      : "border-neutral-200 text-neutral-700"
                  } disabled:opacity-60`}
                >
                  Manual
                </button>
                <button
                  type="button"
                  disabled={Boolean(returnId)}
                  onClick={() => {
                    setSourceType("goods_receipt")
                    setSelectedGR(null)
                    setGrItems([])
                    setErrors((prev) => ({ ...prev, receipt_id: undefined, items: undefined }))
                  }}
                  className={`px-4 py-3 text-xs font-semibold border rounded-xl ${
                    sourceType === "goods_receipt"
                      ? "border-brand-900 bg-brand-900 text-white"
                      : "border-neutral-200 text-neutral-700"
                  } disabled:opacity-60`}
                >
                  Goods Receipt
                </button>
              </div>
            </div>

            <div className="col-span-2">
              <label className="block text-xs text-neutral-700">
                Goods Receipt (POSTED)
                {sourceType === "goods_receipt" && <sup className="text-xs text-red-700"> *</sup>}
              </label>
              <div className="flex gap-2">
                <div className="flex-1">
                  <Input
                    disabled
                    value={
                      selectedGR
                        ? `${selectedGR?.gr_number || selectedGR?.id} - ${
                            selectedGR?.purchase_order?.po_number || selectedGR?.po_id || "-"
                          }`
                        : ""
                    }
                    placeholder={
                      sourceType === "goods_receipt"
                        ? outletId
                          ? "Pilih GR..."
                          : "Pilih outlet terlebih dahulu..."
                        : "Mode manual tidak membutuhkan GR"
                    }
                  />
                </div>
                <button
                  type="button"
                  disabled={!outletId || Boolean(returnId) || sourceType !== "goods_receipt"}
                  onClick={() => setGrModalOpen(true)}
                  className={`px-4 py-3 text-xs font-semibold text-white rounded-xl ${
                    outletId && !returnId && sourceType === "goods_receipt"
                      ? "bg-brand-900 hover:bg-brand-800"
                      : "bg-neutral-400 cursor-not-allowed"
                  }`}
                >
                  Pilih GR
                </button>
              </div>
              <ErrorMessage error={errors?.receipt_id} />
            </div>

            <div className="col-span-2">
              <label className="block text-xs text-neutral-700">{lang.notes}</label>
              <textarea
                disabled={Boolean(returnId)}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                className="block w-full px-4 py-2 mt-1 text-sm transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200 disabled:bg-neutral-200"
                placeholder="Alasan return (opsional)"
              />
              <ErrorMessage error={errors?.header} />
            </div>

            <div className="col-span-2">
              <label className="block text-xs text-neutral-700">{lang.status}</label>
              <Input disabled value={status || "DRAFT"} />
            </div>

          </div>
        </div>

        {/* ── Items section ────────────────────────────────────────────────── */}
        <div className="border border-neutral-200 rounded-xl">
          <div className="px-4 py-5 sm:p-6 space-y-3">

            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-neutral-900">Pilih Produk Return</div>
                <div className="text-xs text-neutral-500">
                  {sourceType === "goods_receipt"
                    ? "Checklist produk dari GR, isi qty_returned (integer)."
                    : "Pilih produk dari stok outlet, lalu isi qty return (integer)."}
                </div>
              </div>
              <div className="text-xs text-neutral-600">
                Dipilih: <b>{selectedCount}</b> | Total Qty: <b>{selectedQtyTotal}</b> | Nilai HPP:{" "}
                <b>
                  {grItems
                    .reduce((acc, x) => acc + (x.checked ? num(x.qty_selected) * num(x.unit_cost) : 0), 0)
                    .toLocaleString("id-ID", { maximumFractionDigits: 0 })}
                </b>
              </div>
            </div>

            <ErrorMessage error={errors?.items} />

            {sourceType === "manual" && (
              <div className="mb-3">
                <label className="block text-xs text-neutral-700">Supplier Tujuan Retur</label>
                <select
                  disabled={isLocked}
                  value={supplierId}
                  onChange={(e) => setSupplierId(e.target.value)}
                  className="w-full px-3 py-2 mt-1 text-xs border border-neutral-200 rounded-xl bg-white disabled:opacity-60"
                >
                  <option value="">Default (Konsinyasi Atalla)</option>
                  {supplierOptions.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-[11px] text-neutral-500">
                  Pilih supplier internal (mis. Vision) supaya stok supplier bertambah kembali saat retur diposting.
                </p>
                {isCrossEntityOutlet && (
                  <p className="mt-1 text-[11px] text-amber-700">
                    Outlet ini milik entitas lain — supplier tujuan wajib supplier internal entitas
                    Anda supaya barang kembali ke stok entitas Anda.
                  </p>
                )}
              </div>
            )}

            {sourceType === "manual" && (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,1fr)_120px_140px_140px]">
                <div>
                  <label className="block text-xs text-neutral-700">Product Unit</label>
                  <SelectSearchable
                    disabled={isLocked || !outletId}
                    filter={(value) =>
                      value?.product_unit?.long_name ||
                      value?.product_unit?.name ||
                      value?.product_unit_long_name ||
                      value?.product_unit_name ||
                      value?.name ||
                      ""
                    }
                    onChange={(value, row) => {
                      const nextId = String(value || "")
                      setSelectedProductUnitId(nextId)
                      setSelectedProductRow(row || null)
                    }}
                    onSearchChange={onManualProductSearchChange}
                    selection={manualProductRows}
                    isLoading={isLoadingManualProducts}
                    value={selectedProductUnitId}
                    keyValue={(value) => String(value?.product_unit_id ?? value?.id ?? "")}
                    title={(value) =>
                      value?.product_unit?.long_name ||
                      value?.product_unit?.name ||
                      value?.product_unit_long_name ||
                      value?.product_unit_name ||
                      value?.name ||
                      "-"
                    }
                    description={(value) =>
                      `Stock: ${num(
                        value?.current_stock ??
                          value?.available_stock ??
                          value?.stock ??
                          value?.qty_on_hand
                      )}`
                    }
                    placeholder={outletId ? "Cari product unit..." : "Pilih outlet terlebih dahulu..."}
                    serverSearch
                    searchValue={productSearchInput}
                    clearOnClose={false}
                  />
                  {/* <div className="mt-2 flex items-center justify-between text-[11px] text-neutral-500">
                    <span>
                      Page: <b>{manualProductMeta?.current_page ?? manualProductPage}</b>
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        disabled={!manualProductMeta?.prev_page_url || isLoadingManualProducts}
                        onClick={() => setManualProductPage((p) => Math.max(1, p - 1))}
                        className="px-2 py-1 border border-neutral-200 rounded-md disabled:opacity-50"
                      >
                        Prev
                      </button>
                      <button
                        type="button"
                        disabled={!manualProductMeta?.next_page_url || isLoadingManualProducts}
                        onClick={() => setManualProductPage((p) => p + 1)}
                        className="px-2 py-1 border border-neutral-200 rounded-md disabled:opacity-50"
                      >
                        Next
                      </button>
                    </div>
                  </div> */}
                </div>

                <div>
                  <label className="block text-xs text-neutral-700">Qty</label>
                  <input
                    type="number"
                    min={1}
                    step={1}
                    disabled={isLocked || !outletId}
                    value={manualQty}
                    onChange={(e) => setManualQty(e.target.value)}
                    className="block w-full px-4 py-2 mt-1 text-sm transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200 disabled:bg-neutral-200"
                  />
                </div>

                <div className="flex items-end">
                  <button
                    type="button"
                    disabled={isLocked || !outletId}
                    onClick={addManualItem}
                    className={`w-full px-4 py-3 text-xs font-semibold text-white rounded-xl ${
                      !isLocked && outletId
                        ? "bg-brand-900 hover:bg-brand-800"
                        : "bg-neutral-400 cursor-not-allowed"
                    }`}
                  >
                    Tambah Produk
                  </button>
                </div>

                {/* 270726 (permintaan user): pilih banyak produk sekaligus — sama
                    seperti form pembelian. */}
                <div className="flex items-end">
                  <button
                    type="button"
                    disabled={isLocked || !outletId}
                    onClick={() => {
                      if (!outletId) return toast.error("Pilih outlet terlebih dahulu")
                      setShowBulkPicker(true)
                    }}
                    className={`w-full px-4 py-3 text-xs font-semibold rounded-xl border ${
                      !isLocked && outletId
                        ? "border-brand-900 text-neutral-900 hover:bg-neutral-100"
                        : "border-neutral-200 text-neutral-400 cursor-not-allowed"
                    }`}
                  >
                    Pilih Banyak
                  </button>
                </div>
              </div>
            )}

            {/* Mount kondisional: fetch di modal hanya hidup saat terbuka. */}
            {showBulkPicker && (
              <ReturnProductPickerModal
                open
                onClose={() => setShowBulkPicker(false)}
                onAdd={addManualItemsBulk}
                outletId={outletId?.id ?? outletId}
                uom={retUom}
                factor={retFactor}
              />
            )}

            {/* 240726 (#7): satuan GLOBAL retur — input qty dalam satuan ini. */}
            <div className="flex items-center gap-2 mb-3">
              <label className="text-xs font-medium text-neutral-600">Satuan (semua item)</label>
              <select
                value={retUom}
                disabled={isLocked}
                onChange={(e) => {
                  const next = e.target.value
                  setRetUom(next)
                  // 290726: harga item MANUAL dihitung ulang dari harga DASAR per PCS.
                  // Item dari Goods Receipt tidak disentuh — harganya mengikuti harga
                  // beli asli dan memang dikunci.
                  setGrItems((prev) =>
                    prev.map((it) =>
                      it.is_manual
                        ? {
                            ...it,
                            unit_cost: costPerPcsFor(next, it.base_pcs_price, it.dozen_store_price),
                          }
                        : it
                    )
                  )
                }}
                className="w-32 px-3 py-2 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring focus:ring-neutral-200 disabled:bg-neutral-200"
              >
                {/* 270726: opsi dari MASTER satuan (pola form pembelian); satuan retur
                    lama yang tak ada di master tetap diinjeksi supaya value select cocok. */}
                {((satuanOptions ?? RETUR_UOM_OPTIONS).includes(retUom)
                  ? (satuanOptions ?? RETUR_UOM_OPTIONS)
                  : [retUom, ...(satuanOptions ?? RETUR_UOM_OPTIONS)]
                ).map((u) => (
                  <option key={u} value={u}>{u}</option>
                ))}
              </select>
            </div>

            <div className="overflow-x-auto border border-neutral-200 rounded-xl">
              <table className="min-w-full divide-y divide-neutral-200">
                <thead className="bg-neutral-50">
                  <tr>
                    <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">Pilih</th>
                    <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">Product Unit</th>
                    <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">
                      {sourceType === "goods_receipt" ? "Qty Diterima" : "Stok Saat Ini"}
                    </th>
                    <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">Batch</th>
                    <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">Expired</th>
                    <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">Qty Return</th>
                    <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">Satuan</th>
                    <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">Unit Cost</th>
                    <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">Catatan</th>
                  </tr>
                </thead>

                <tbody className="bg-white divide-y divide-neutral-200">
                  {sourceType === "goods_receipt" && !receiptId && (
                    <tr>
                      <td colSpan={9} className="px-3 py-6 text-xs text-center text-neutral-500">
                        Pilih outlet lalu pilih GR untuk memunculkan item.
                      </td>
                    </tr>
                  )}

                  {sourceType === "manual" && grItems.length === 0 && (
                    <tr>
                      <td colSpan={9} className="px-3 py-6 text-xs text-center text-neutral-500">
                        Pilih outlet lalu tambahkan produk dari stok outlet.
                      </td>
                    </tr>
                  )}

                  {sourceType === "goods_receipt" && receiptId && grItems.length === 0 && (
                    <tr>
                      <td colSpan={9} className="px-3 py-6 text-xs text-center text-neutral-500">
                        Item GR belum termuat. Pastikan endpoint{" "}
                        <b>GET /goods-receipts/{receiptId}</b> mengembalikan <b>item</b>.
                      </td>
                    </tr>
                  )}

                  {grItems.map((it) => (
                    <tr key={it.id} className="hover:bg-brand-50 transition-colors">
                      <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                        <input
                          type="checkbox"
                          disabled={isLocked}
                          checked={Boolean(it.checked)}
                          onChange={(e) => toggleItem(it.id, e.target.checked)}
                        />
                      </td>

                      <td className="px-3 py-4 text-xs font-medium text-neutral-900 whitespace-nowrap">
                        {it.product_unit}
                      </td>

                      <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                        {it.qty_received ?? "-"}
                      </td>

                      <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                        {it.batch_no || "-"}
                      </td>

                      <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                        {it.expired_date || "-"}
                      </td>

                      <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                        {/* 240726 (#7): input qty dalam satuan global (retUom); nilai
                            internal qty_selected tetap PCS (× faktor saat onChange). */}
                        <input
                          type="number"
                          min={0}
                          step="any"
                          disabled={isLocked || !it.checked}
                          value={it.checked ? qtyUomStr(it.qty_selected, retFactor) : 0}
                          onChange={(e) => {
                            const rawPcs = Math.round(num(e.target.value) * retFactor)
                            const maxQty = num(it.qty_returnable ?? it.qty_received)
                            const nextQty = Math.max(0, Math.min(rawPcs, maxQty))
                            updateItem(it.id, { qty_selected: nextQty })
                          }}
                          className="w-24 px-3 py-2 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring focus:ring-neutral-200 disabled:bg-neutral-200"
                        />
                        <div className="mt-1 text-[11px] text-neutral-500">
                          Max: {qtyUomStr(num(it.qty_returnable ?? it.qty_received), retFactor)} {retUom}
                          {retFactor > 1 ? ` (${num(it.qty_returnable ?? it.qty_received)} pcs)` : ""}
                        </div>
                      </td>

                      <td className="px-3 py-4 text-xs font-medium text-neutral-700 whitespace-nowrap">
                        {retUom}
                      </td>

                      <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                        <input
                          type="number"
                          min={0}
                          step={1}
                          // disabled={isLocked || !it.checked}
                          disabled={sourceType === "goods_receipt" || isLocked || !it.checked}
                          // 290726 (laporan user): harga ikut SATUAN terpilih. Pola sama
                          // dgn kolom Qty di atas — nilai TERSIMPAN tetap per PCS
                          // (subtotal = qty_pcs x cost_pcs), yang dikonversi hanya
                          // tampilannya: per-satuan = cost_pcs x faktor.
                          value={
                            it.unit_cost === "" || it.unit_cost == null
                              ? ""
                              : Math.round(num(it.unit_cost) * retFactor)
                          }
                          onChange={(e) => {
                            const perUom = e.target.value
                            updateItem(it.id, {
                              unit_cost: perUom === "" ? "" : Math.round(num(perUom) / retFactor),
                            })
                          }}
                          className="w-24 px-3 py-2 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring focus:ring-neutral-200 disabled:bg-neutral-200"
                        />
                      </td>

                      <td className="px-3 py-4 text-xs text-neutral-700">
                        <input
                          disabled={isLocked || !it.checked}
                          value={it.notes ?? ""}
                          onChange={(e) => updateItem(it.id, { notes: e.target.value })}
                          className="w-64 px-3 py-2 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring focus:ring-neutral-200 disabled:bg-neutral-200"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="text-xs text-neutral-500">
              * Validasi maksimal returnable + stok dilakukan saat <b>Post</b> oleh backend.
            </div>
          </div>
        </div>

      </div>

      {/* ── GR picker modal ───────────────────────────────────────────────── */}
      {grModalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-end p-4">
          <div
            className="absolute inset-0 z-[9998] bg-black/40"
            onClick={() => setGrModalOpen(false)}
          />
          <div className="relative z-[9999] w-full max-w-5xl bg-white rounded-2xl shadow-xl border border-neutral-200 overflow-hidden">

            <div className="p-4 border-b border-neutral-200 flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-neutral-900">Pilih Goods Receipt (POSTED)</div>
                <div className="text-xs text-neutral-500">
                  Ter-filter outlet_id = {outletId?.id ?? outletId}.
                </div>
              </div>
              <button
                type="button"
                onClick={() => setGrModalOpen(false)}
                className="px-3 py-2 text-xs font-semibold text-neutral-700 border border-neutral-200 rounded-xl hover:bg-neutral-50"
              >
                Tutup
              </button>
            </div>

            <div className="p-4 space-y-3">
              <div className="relative">
                <div className="absolute inset-y-0 flex items-center pl-3 pointer-events-none">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-4 h-4"
                    viewBox="0 0 24 24"
                    strokeWidth={1}
                    stroke="currentColor"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <circle cx={10} cy={10} r={7} />
                    <line x1={21} y1={21} x2={15} y2={15} />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder={`${lang.search} Goods Receipt`}
                  value={searchInputValue}
                  onChange={(e) => onSearchChange(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onSearchSubmit() } }}
                  className="w-full py-3 pl-8 pr-4 text-xs transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200"
                />
              </div>

              <div className="overflow-x-auto border border-neutral-200 rounded-xl">
                <table className="min-w-full divide-y divide-neutral-200">
                  <thead className="bg-neutral-50">
                    <tr>
                      <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">GR No</th>
                      <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">PO</th>
                      <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Supplier</th>
                      <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Tanggal</th>
                    </tr>
                  </thead>

                  <tbody className="bg-white divide-y divide-neutral-200">
                    {isLoadingGR && (
                      <tr>
                        <td colSpan={4} className="px-3 py-6 text-xs text-center text-neutral-500">
                          {lang.loading_data}
                        </td>
                      </tr>
                    )}

                    {!isLoadingGR && grList.length === 0 && (
                      <tr>
                        <td colSpan={4} className="px-3 py-6 text-xs text-center text-neutral-500">
                          {lang.no_data}
                        </td>
                      </tr>
                    )}

                    {!isLoadingGR &&
                      grList.map((gr) => (
                        <tr
                          key={gr.id}
                          onClick={() => pickGR(gr)}
                          className="cursor-pointer hover:bg-neutral-50"
                        >
                          <td className="px-3 py-4 text-xs font-medium text-neutral-900 whitespace-nowrap">
                            {gr.gr_number ?? gr.id}
                          </td>
                          <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                            {gr.po_number ?? gr.po_id ?? "-"}
                          </td>
                          <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                            {gr.supplier_name ?? gr.supplier_name_snapshot ?? gr.supplier_id ?? "-"}
                          </td>
                          <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                            {gr.receipt_date ?? gr.created_at ?? "-"}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between">
                <div className="text-xs text-neutral-500">
                  Halaman: <b>{page}</b>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={!canPrev}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="px-3 py-2 text-xs font-semibold text-neutral-700 border border-neutral-200 rounded-xl disabled:opacity-50"
                  >
                    Sebelumnya
                  </button>
                  <button
                    type="button"
                    disabled={!canNext}
                    onClick={() => setPage((p) => p + 1)}
                    className="px-3 py-2 text-xs font-semibold text-neutral-700 border border-neutral-200 rounded-xl disabled:opacity-50"
                  >
                    Selanjutnya
                  </button>
                </div>
              </div>

              <div className="text-[11px] text-neutral-500">
                * Daftar otomatis difilter: outlet_id + status POSTED.
              </div>
            </div>
          </div>
        </div>
      )}

      <PurchaseReturnPrintDocument
        ref={printRef}
        returnNumber={returnNumber}
        status={status}
        sourceType={sourceType}
        grNumber={grNumber}
        poNumber={poNumber}
        outletName={outletName}
        supplierName={supplierName}
        returnDate={returnDate}
        reason={reason}
        items={printItems}
        totalQty={printTotalQty}
        totalAmount={printTotalAmount}
        printedBy={user?.name}
      />
    </Transition>
  )
}
